// Types and helpers for the Contract Templates configuration page and the click-to-place editor.

export type TemplateKind = 'base_contract' | 'addendum' | 'commission';
export type TemplateMode = 'fillable' | 'flat';
export type TemplateFieldKind = 'text' | 'checkbox';

export interface ContractVariable {
  key: string;
  label: string;
  group: string;
}

export interface ContractTemplateField {
  id?: string;
  variable: string | null;
  field_kind: TemplateFieldKind;
  pdf_field_name?: string | null;
  page?: number | null;
  x?: number | null;
  y?: number | null;
  font_size?: number | null;
  on_value?: string | null;
  static_value?: string | null;
}

export interface ContractTemplate {
  id: string;
  name: string;
  kind: TemplateKind;
  mode: TemplateMode;
  form_number: string | null;
  version: string | null;
  state: string | null;
  addendum_tag: string | null;
  page_count: number | null;
  active: boolean;
  system: boolean;
  editable: boolean;
  field_count: number;
  fields?: ContractTemplateField[];
}

export interface PdfFormField {
  name: string;
  type: string;
  page: number;
  rect: number[];
}

export interface PdfInspection {
  page_count: number;
  pages: { number: number; width: number; height: number }[];
  fields: PdfFormField[];
  mode: TemplateMode;
}

export const KIND_LABELS: Record<TemplateKind, string> = {
  base_contract: 'Base contract',
  addendum: 'Addendum',
  commission: 'Broker-to-broker commission',
};

// Groups the variable list for a <select> with <optgroup>s, keeping the server's order.
export const groupVariables = (variables: ContractVariable[]): { group: string; items: ContractVariable[] }[] => {
  const groups: { group: string; items: ContractVariable[] }[] = [];
  variables.forEach((variable) => {
    let entry = groups.find((g) => g.group === variable.group);
    if (!entry) {
      entry = { group: variable.group, items: [] };
      groups.push(entry);
    }
    entry.items.push(variable);
  });
  return groups;
};

// A click on the rendered page (pixels from the top-left of the canvas) becomes a spot on the
// PDF (points from the bottom-left of the page), which is how PDFs measure positions.
export const clickToPdfPoint = (
  clickX: number, clickY: number, scale: number, pageHeightPoints: number
): { x: number; y: number } => ({
  x: Math.round((clickX / scale) * 10) / 10,
  y: Math.round((pageHeightPoints - clickY / scale) * 10) / 10,
});

// The reverse: where to draw a marker on the rendered page for a saved spot.
export const pdfPointToCanvas = (
  x: number, y: number, scale: number, pageHeightPoints: number
): { left: number; top: number } => ({
  left: x * scale,
  top: (pageHeightPoints - y) * scale,
});

// Only rows with something to print are saved; blank rows are just "not mapped".
export const buildFieldsPayload = (fields: ContractTemplateField[]): ContractTemplateField[] =>
  fields
    .filter((f) => (f.variable && f.variable !== '') || (f.static_value && f.static_value !== ''))
    .map((f) => {
      const payload: ContractTemplateField = {
        variable: f.variable || null,
        field_kind: f.field_kind,
        pdf_field_name: f.pdf_field_name ?? null,
        page: f.page ?? null,
        x: f.x ?? null,
        y: f.y ?? null,
        font_size: f.font_size ?? null,
        on_value: f.field_kind === 'checkbox' ? (f.on_value || null) : null,
        static_value: f.static_value || null,
      };
      return payload;
    });

// Which PDF form fields are checkboxes (so the editor asks "tick when value is...").
export const isCheckboxType = (type: string): boolean => type === 'checkbox' || type === 'radio';
